import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import { REQUEST_LOADING } from 'constants/alerts';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { useEffect } from 'react';
import { UserService } from 'services/UserService';
import { SectionTitle } from 'ui/SectionTitle';
import {
  onHandlePhoneChange,
  phoneFormatterKeyDown,
} from 'utils/forms/formatter';
import { passwordFields } from 'utils/forms/password';
import {
  emailRule,
  iinRule,
  onlyNumberRule,
  requiredRule,
} from 'utils/forms/validationRules';

function UserPartForm({ initialValues, onMoveNext, onFetch, onSubmit }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialValues) {
      const { bin_iin, full_name, email, phone } = initialValues;

      form.setFieldsValue({
        bin_iin,
        full_name,
        email,
        phone,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = async ({
    bin_iin,
    email,
    full_name,
    phone,
    password,
  }) => {
    const payload = {
      bin_iin,
      username: email,
      email,
      full_name,
      password,
      phone: phone.replace(/\D/g, ''),
    };

    const hide = message.loading(REQUEST_LOADING);

    try {
      let userId;

      if (initialValues) {
        userId = await UserService.updateUser(initialValues?.id, payload);
      } else {
        userId = await UserService.createUser({
          ...payload,
          is_staff: true,
          password_set_by_admin: true,
        });
      }

      if (userId) {
        form.resetFields();
        onSubmit(userId);
        onMoveNext();
        onFetch();
        message.success(
          `Пользователь успешно ${
            initialValues ? 'отредактирован' : 'добавлен'
          }`
        );
      }
    } catch (error) {
      message.error(
        `Не удалось ${
          initialValues ? 'отредактировать' : 'добавить'
        } пользователя`
      );
    } finally {
      hide();
    }
  };

  return (
    <Form
      form={form}
      labelCol={{
        span: LABEL_SPAN,
      }}
      wrapperCol={{ span: CONTENT_SPAN }}
      layout="horizontal"
      labelAlign="right"
      onFinish={handleSubmit}
    >
      <SectionTitle>Основные данные</SectionTitle>

      <Form.Item
        name="bin_iin"
        label="ИИН/БИН"
        rules={[requiredRule, onlyNumberRule, iinRule]}
      >
        <Input maxLength={12} placeholder="Введите ИИН/БИН" allowClear />
      </Form.Item>

      <Form.Item name="full_name" label="ФИО" rules={[requiredRule]}>
        <Input placeholder="Введите ФИО" allowClear />
      </Form.Item>

      <Form.Item
        name="email"
        label="Эл. почта"
        rules={[requiredRule, emailRule]}
      >
        <Input placeholder="Введите почту" allowClear />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Телефон номер"
        rules={[requiredRule, { len: 18, message: 'Введите полностью номер' }]}
      >
        <Input
          onChange={(e) => onHandlePhoneChange(e, form)}
          onKeyDown={(e) => phoneFormatterKeyDown(e, form)}
          type="tel"
          placeholder="+ 7 ( _ _ _ ) _ _ _  _ _  _ _ "
          allowClear
        />
      </Form.Item>

      {!initialValues && <SectionTitle>Пароль</SectionTitle>}

      {!initialValues &&
        passwordFields.map(
          ({ name, label, placeholder, rules, dependencies }) => (
            <Form.Item
              key={name}
              name={name}
              label={label}
              rules={rules}
              hasFeedback
              dependencies={dependencies}
            >
              <Input.Password
                allowClear
                placeholder={placeholder}
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
          )
        )}

      <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
        <Button type="primary" htmlType="submit">
          Сохранить
        </Button>
        {initialValues && (
          <Button style={{ marginLeft: '8px' }} onClick={() => onMoveNext()}>
            Дальше
          </Button>
        )}
      </Form.Item>
    </Form>
  );
}

export { UserPartForm };
